import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { useObservableState } from 'observable-hooks';
import { useHistory, useParams } from 'react-router-dom';
import { numberTransform, route } from '../../../../../services/utils';
import { useService } from '../../../../../services/services';
import { map } from 'rxjs/operators';
import { CROP_FIELD_VIEW, CROP_FIELDS_AND_WORKS_INDEX } from '../../../../../router/routes';
import FieldAnalyses from '../../../../../interfaces/fieldAnalyses';
import DateElement from '../../../../../components/form/elements/date';
import Decimal from '../../../../../components/form/elements/decimal';
import Group from '../../../../../components/form/elements/group';
import Link from '../../../../../components/form/elements/link';
import Select, { Option } from '../../../../../components/form/elements/select';
import Text from '../../../../../components/form/elements/text';
import Button from '../../../../../components/form/elements/button';
import Form, { SubmitType } from '../../../../../components/form';
import ClassifierGroup from '../../../../../enums/classifier-group';
import ClassifierValue from '../../../../../interfaces/classifierValue';
import Classifier from '../../../../../enums/classifier';
import TabsKeys from '../../../../../enums/tab-keys';
import css from './analyses.module.css';
import Percentage from '../../../../../components/form/elements/percentage';
import { Section } from '../../index';

export default function ViewAnalyses() {
  const history = useHistory();
  const { farmId, season, id } = useParams<{
    farmId: string;
    season: string;
    id: string;
  }>();
  const { fieldAnalysesService, classifierService } = useService();
  const [body, setBody] = useState([]);
  const [footer, setFooter] = useState([]);

  const transKeyPrefix = 'pages.crop.fields_and_works.fields.analyses.';

  const schema = yup.object().shape({
    date: yup.date().required(),
    soil_granulometric_composition_id: yup.string().required().uuid(),
    organic_substances: yup
      .number()
      .percentage()
      .nullable()
      .transform(numberTransform),
    pH: yup.number().positive().decimal().nullable().transform(numberTransform),
    P2O5: yup
      .number()
      .positive()
      .decimal()
      .nullable()
      .transform(numberTransform),
    K2O: yup
      .number()
      .positive()
      .decimal()
      .nullable()
      .transform(numberTransform),
    other_indicator: yup.string().max(255).nullable(),
    other_value: yup
      .number()
      .positive()
      .decimal()
      .nullable()
      .transform(numberTransform),
  });

  const [fieldAnalyses] = useObservableState<FieldAnalyses>(
    () => fieldAnalysesService.current(farmId, id),
    null
  );

  const [soilGranulometricComposition] = useObservableState<Option[]>(
    () =>
      classifierService
        .values(ClassifierGroup.CROP, Classifier.SOIL_GRANULOMETRIC_COMPOSITION)
        .pipe(
          map((values: ClassifierValue[]) =>
            values.map((value) => {
              return { value: value.id, label: value.value };
            })
          )
        ),
    null
  );

  const submit = useCallback<SubmitType<FieldAnalyses>>(
    (form: any) => {
      return fieldAnalyses.id &&
        new Date(fieldAnalyses.date).getTime() === form.date.getTime()
        ? [
            fieldAnalysesService.update(farmId, id, fieldAnalyses.id, form),
            (response: FieldAnalyses) =>
              history.push(
                route(CROP_FIELD_VIEW, {
                  farmId: farmId,
                  season: season,
                  id: id,
                  tab: TabsKeys.analyses,
                })
              ),
          ]
        : [
            fieldAnalysesService.store(farmId, id, form),
            (response: FieldAnalyses) =>
              history.push(
                route(CROP_FIELD_VIEW, {
                  farmId: farmId,
                  season: season,
                  id: id,
                  tab: TabsKeys.analyses,
                })
              ),
          ];
    },
    [fieldAnalysesService, history, farmId, season, id, fieldAnalyses]
  );

  useEffect(() => {
    if (soilGranulometricComposition !== null) {
      setBody([
        Group([
          DateElement('date', transKeyPrefix + 'date'),
          Select('soil_granulometric_composition_id', {
            options: soilGranulometricComposition,
            multi: false,
            label: transKeyPrefix + 'soil_granulometric_composition',
          }),
          Percentage(
            'organic_substances',
            transKeyPrefix + 'organic_substances'
          ),
          Decimal('pH', transKeyPrefix + 'pH'),
          Decimal('P2O5', transKeyPrefix + 'P2O5'),
          Decimal('K2O', transKeyPrefix + 'K2O'),
          Text('other_indicator', transKeyPrefix + 'other_indicator'),
          Decimal('other_value', transKeyPrefix + 'other_value'),
        ]),
      ]);

      setFooter([
        Button('save'),
        Link(
          route(CROP_FIELDS_AND_WORKS_INDEX, {
            farmId: farmId,
            season: season,
            tab: Section.fields,
          }),
          'cancel'
        ),
      ]);
    }
  }, [
    fieldAnalyses,
    setBody,
    setFooter,
    farmId,
    season,
    id,
    soilGranulometricComposition,
  ]);

  return (
    <div>
      <div className={css['content']}>
        <Form
          title={''}
          mode={fieldAnalyses && fieldAnalyses.date ? 'edit' : 'create'}
          body={body}
          footer={footer}
          values={fieldAnalyses}
          className={css['analyses-form']}
          submit={submit}
          schema={schema}
          hideHeader={true}
          noticeAccessor="ViewForm"
        />
      </div>
    </div>
  );
}
